import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Button,
    Calendar,
    Card,
    Col,
    Empty,
    Input,
    List,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Tag,
    Typography,
} from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    LeftOutlined,
    ReloadOutlined,
    RightOutlined,
    SearchOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'underscore';
import dayjs, { Dayjs } from 'dayjs';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getSingleSchool } from '../../core/school/schoolApi';
import { getSchoolIndicatorsCalendar } from '../../core/indicator/indicatorApi';
import { SchoolCalendarIndicator } from '../../core/indicator/types';
import { isCollectionPeriodMissed } from '../../core/utils/helpers';

import './schoolIndicatorsCalendar.scss';

const { Title, Paragraph, Text } = Typography;

const CRITICAL_FILTER_ROLES = [1, 2, 3, 4]; // admin, švietimo skyrius, švietimo centras

type CalendarDepartment = 'education_center' | 'education_department';

interface SchoolIndicatorsCalendarProps {
    department?: false | CalendarDepartment;
}

type DayBucket = {
    start: SchoolCalendarIndicator[];
    end: SchoolCalendarIndicator[];
};

const SchoolIndicatorsCalendar = ({ department = false }: SchoolIndicatorsCalendarProps) => {
    const dispatch = useDispatch<AppDispatch>();

    const { userData } = useSelector((state: CombinedStates) => state.user);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);
    const { isFetching, schoolCalendarIndicators } = useSelector((state: CombinedStates) => state.indicator);

    const [panelDate, setPanelDate] = useState<Dayjs>(() => dayjs().startOf('month'));
    const [selectedDate, setSelectedDate] = useState<Dayjs>(() => dayjs());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
    const [onlyCritical, setOnlyCritical] = useState(false);

    const canFilterCritical = CRITICAL_FILTER_ROLES.includes(userData?.role_id || 0);

    const getYearForPeriodAndMonth = useCallback((period: string | null | undefined, month: number) => {
        if (!period) return dayjs().year();

        const parts = period.split('-');

        if (period.match(/^\d{4}-\d{4}$/)) {
            const startYear = Number(parts[0]);
            const endYear = Number(parts[1]);
            return month >= 9 ? startYear : endYear;
        }

        if (period.match(/^\d{4}-\d{2}$/)) {
            return parseInt(parts[0] || '0', 10);
        }

        if (period.match(/^Q\d-\d{4}$/)) {
            return parseInt(parts[1] || '0', 10);
        }

        if (period.match(/pusmetis/)) {
            const yearMatch = period.match(/(\d{4})-(\d{4})/);
            if (yearMatch) {
                const startYear = Number(yearMatch[1]);
                const endYear = Number(yearMatch[2]);
                return month >= 9 ? startYear : endYear;
            }
            return dayjs().year();
        }

        return parseInt(period, 10) || dayjs().year();
    }, []);

    const toCollectionDate = useCallback((indicator: SchoolCalendarIndicator, mmdd: string | null | undefined) => {
        if (!mmdd) return null;
        // Support full date too (YYYY-MM-DD), if backend returns it
        if (mmdd.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const d = dayjs(mmdd);
            return d.isValid() ? d : null;
        }
        const [mmStr, ddStr] = mmdd.split('-');
        const month = parseInt(mmStr || '0', 10);
        const day = parseInt(ddStr || '0', 10);
        if (!month || !day) return null;

        const year = getYearForPeriodAndMonth(indicator.period, month);
        const full = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return dayjs(full);
    }, [getYearForPeriodAndMonth]);

    const hasValue = useCallback((ind: SchoolCalendarIndicator) => (
        ind.value !== null && ind.value !== undefined && ind.value !== ''
    ), []);

    useEffect(() => {
        if (department) return;
        if (isEmpty(singleSchool)) {
            dispatch(getSingleSchool({}));
        }
    }, [department]);

    const basePayload = useMemo(() => {
        if (department) {
            return { department };
        }

        if (userData?.role_id === 5) {
            return {};
        }

        return singleSchool?.id ? { school_id: singleSchool.id } : null;
    }, [department, userData?.role_id, singleSchool?.id]);

    const fetchCalendar = useCallback(() => {
        if (!basePayload) return;
        // date nesiunčiame: API pagal nutylėjimą naudoja šiandieną
        dispatch(getSchoolIndicatorsCalendar({
            ...basePayload,
            ...(canFilterCritical && onlyCritical ? { only_critical: true } : {}),
        }));
    }, [dispatch, basePayload, canFilterCritical, onlyCritical]);

    useEffect(() => {
        if (!basePayload) return;
        fetchCalendar();
    }, [basePayload, onlyCritical]);

    const byDay = useMemo(() => {
        const map = new Map<string, DayBucket>();

        (schoolCalendarIndicators || []).forEach((ind) => {
            const startDate = toCollectionDate(ind, ind.collection_start);
            const endDate = toCollectionDate(ind, ind.collection_end);
            const start = startDate ? startDate.format('YYYY-MM-DD') : null;
            const end = endDate ? endDate.format('YYYY-MM-DD') : null;

            if (start) {
                const prev = map.get(start) || { start: [], end: [] };
                prev.start.push(ind);
                map.set(start, prev);
            }

            if (end) {
                const prev = map.get(end) || { start: [], end: [] };
                prev.end.push(ind);
                map.set(end, prev);
            }
        });

        return map;
    }, [schoolCalendarIndicators, toCollectionDate]);

    const today = useMemo(() => dayjs().startOf('day'), []);

    const overdueIndicators = useMemo(() => (schoolCalendarIndicators || [])
        .filter(ind => isCollectionPeriodMissed(ind))
        .sort((a, b) => {
            const aEnd = toCollectionDate(a, a.collection_end)?.valueOf() || 0;
            const bEnd = toCollectionDate(b, b.collection_end)?.valueOf() || 0;
            return aEnd - bEnd;
        }), [schoolCalendarIndicators, toCollectionDate]);

    const activeIndicators = useMemo(() => (schoolCalendarIndicators || [])
        .filter(ind => !hasValue(ind))
        .filter(ind => {
            const start = toCollectionDate(ind, ind.collection_start);
            const end = toCollectionDate(ind, ind.collection_end);
            if (!start || !end) return false;

            const isAfterStart = today.isSame(start, 'day') || today.isAfter(start, 'day');
            const isBeforeEnd = today.isSame(end, 'day') || today.isBefore(end, 'day');
            return isAfterStart && isBeforeEnd;
        })
        .sort((a, b) => {
            const aEnd = toCollectionDate(a, a.collection_end)?.valueOf() || 0;
            const bEnd = toCollectionDate(b, b.collection_end)?.valueOf() || 0;
            return aEnd - bEnd;
        }), [schoolCalendarIndicators, hasValue, today, toCollectionDate]);

    const upcomingIndicators = useMemo(() => (schoolCalendarIndicators || [])
        .filter(ind => !hasValue(ind))
        .filter(ind => !!ind.collection_start)
        .filter(ind => {
            const start = toCollectionDate(ind, ind.collection_start);
            return !!start && start.isAfter(today, 'day');
        })
        .sort((a, b) => {
            const aStart = toCollectionDate(a, a.collection_start)?.valueOf() || 0;
            const bStart = toCollectionDate(b, b.collection_start)?.valueOf() || 0;
            return aStart - bStart;
        }), [schoolCalendarIndicators, hasValue, today, toCollectionDate]);

    const completedIndicators = useMemo(() => (schoolCalendarIndicators || [])
        .filter(ind => hasValue(ind))
        .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'lt')), [schoolCalendarIndicators, hasValue]);

    const categoryOptions = useMemo(() => {
        const map = new Map<number, string>();

        (schoolCalendarIndicators || []).forEach((ind) => {
            ind.categories?.forEach((category) => {
                if (category.id && category.title) {
                    map.set(category.id, category.title);
                }
            });
            if (typeof ind.category_id === 'number' && !map.has(ind.category_id)) {
                map.set(ind.category_id, `Kategorija #${ind.category_id}`);
            }
        });

        return Array.from(map.entries())
            .sort((a, b) => a[1].localeCompare(b[1], 'lt'))
            .map(([id, title]) => ({ value: id, label: title }));
    }, [schoolCalendarIndicators]);

    const matchesSearch = useCallback((item: SchoolCalendarIndicator) => {
        if (selectedCategoryId !== undefined) {
            const inCategory = item.categories?.some(category => category.id === selectedCategoryId)
                || item.category_id === selectedCategoryId;
            if (!inCategory) return false;
        }

        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;

        if (String(item.id).includes(term)) return true;
        if ((item.title || '').toLowerCase().includes(term)) return true;
        if (item.categories?.some(category => (category.title || '').toLowerCase().includes(term))) return true;

        return false;
    }, [searchTerm, selectedCategoryId]);

    const filterIndicators = useCallback(
        (list: SchoolCalendarIndicator[]) => list.filter(matchesSearch),
        [matchesSearch],
    );

    const filteredOverdueIndicators = useMemo(
        () => filterIndicators(overdueIndicators),
        [filterIndicators, overdueIndicators],
    );

    const filteredActiveIndicators = useMemo(
        () => filterIndicators(activeIndicators),
        [filterIndicators, activeIndicators],
    );

    const filteredUpcomingIndicators = useMemo(
        () => filterIndicators(upcomingIndicators),
        [filterIndicators, upcomingIndicators],
    );

    const filteredCompletedIndicators = useMemo(
        () => filterIndicators(completedIndicators),
        [filterIndicators, completedIndicators],
    );

    const hasActiveSearch = !!searchTerm.trim() || selectedCategoryId !== undefined;

    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
        setSelectedCategoryId(undefined);
    }, []);

    const getFilteredEmptyDescription = useCallback((defaultText: string) => (
        hasActiveSearch ? 'Nerasta pagal paieškos kriterijus' : defaultText
    ), [hasActiveSearch]);

    const openDayModal = useCallback((date: Dayjs) => {
        setSelectedDate(date);
        setIsModalOpen(true);
    }, []);

    const cellRender = useCallback((date: Dayjs) => {
        const key = date.format('YYYY-MM-DD');
        const bucket = byDay.get(key);
        if (!bucket) return null;

        const startCount = bucket.start.length;
        const endCount = bucket.end.length;
        const startDone = bucket.start.filter(i => hasValue(i)).length;
        const endDone = bucket.end.filter(i => hasValue(i)).length;

        return (
            <div style={{ marginTop: 4, lineHeight: 1.15 }}>
                {startCount > 0 && (
                    <div>
                        <Badge
                            status={startDone === startCount ? 'success' : 'processing'}
                            text={<span style={{ fontSize: 12 }}>Pradžia: {startDone}/{startCount}</span>} />
                    </div>
                )}
                {endCount > 0 && (
                    <div>
                        <Badge
                            status={endDone === endCount ? 'success' : 'warning'}
                            text={<span style={{ fontSize: 12 }}>Pabaiga: {endDone}/{endCount}</span>} />
                    </div>
                )}
            </div>
        );
    }, [byDay, hasValue]);

    const fullCellRender = useCallback((date: Dayjs) => {
        const content = cellRender(date);

        return (
            <div
                className="ant-picker-cell-inner ant-picker-calendar-date"
                onClick={() => openDayModal(date)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDayModal(date);
                    }
                }}
                role="button"
                tabIndex={0}
                style={{ cursor: 'pointer' }}>
                <div className="ant-picker-calendar-date-value">{date.date()}</div>
                <div className="ant-picker-calendar-date-content">
                    {content}
                </div>
            </div>
        );
    }, [cellRender, openDayModal]);

    const handlePanelChange = useCallback((date: Dayjs) => {
        // Keičiame tik rodomą mėnesį/metus (panelę), be modalo ir be serverio
        setPanelDate(date.startOf('month'));
    }, []);

    const goToPreviousMonth = useCallback(() => {
        setPanelDate(prev => prev.subtract(1, 'month').startOf('month'));
    }, []);

    const goToNextMonth = useCallback(() => {
        setPanelDate(prev => prev.add(1, 'month').startOf('month'));
    }, []);

    const selectedBucket = useMemo(() => {
        const key = selectedDate.format('YYYY-MM-DD');
        return byDay.get(key) || { start: [], end: [] };
    }, [byDay, selectedDate]);

    const isIndicatorActiveOnDate = useCallback((ind: SchoolCalendarIndicator, date: Dayjs) => {
        const start = toCollectionDate(ind, ind.collection_start);
        const end = toCollectionDate(ind, ind.collection_end);
        if (!start || !end) return false;

        const day = date.startOf('day');
        const isAfterStart = day.isSame(start, 'day') || day.isAfter(start, 'day');
        const isBeforeEnd = day.isSame(end, 'day') || day.isBefore(end, 'day');
        return isAfterStart && isBeforeEnd;
    }, [toCollectionDate]);

    const selectedDayActiveIndicators = useMemo(() => (
        (schoolCalendarIndicators || [])
            .filter(ind => isIndicatorActiveOnDate(ind, selectedDate))
            .sort((a, b) => (a.title || '').localeCompare(b.title || '', 'lt'))
    ), [schoolCalendarIndicators, selectedDate, isIndicatorActiveOnDate]);

    const renderCategories = useCallback((item: SchoolCalendarIndicator) => {
        if (item.categories?.length) {
            return item.categories.map(c => c.title).filter(Boolean).join(' / ');
        }
        if (typeof item.category_id === 'number') {
            return `#${item.category_id}`;
        }
        return '-';
    }, []);

    const renderStatusTag = useCallback((item: SchoolCalendarIndicator) => {
        if (item.confirmed_at) return <Tag color="green">Patvirtinta</Tag>;
        if (item.submitted_at) return <Tag color="blue">Pateikta</Tag>;
        if (hasValue(item)) return <Tag color="gold">Suvestas</Tag>;
        return <Tag>Neįvesta</Tag>;
    }, [hasValue]);

    const formatDateTime = useCallback((value?: string | null) => (
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : null
    ), []);

    const renderCategoryTags = useCallback((item: SchoolCalendarIndicator) => {
        if (item.categories?.length) {
            return (
                <Space size={[4, 4]} wrap>
                    {item.categories.map(category => (
                        <Tag key={category.id} style={{ margin: 0 }}>
                            {category.title}
                        </Tag>
                    ))}
                </Space>
            );
        }
        if (typeof item.category_id === 'number') {
            return <Tag>#{item.category_id}</Tag>;
        }
        return <Text type="secondary">-</Text>;
    }, []);

    const renderSubmissionTimeline = useCallback((item: SchoolCalendarIndicator, compact = false) => {
        const submittedAt = formatDateTime(item.submitted_at);
        const confirmedAt = formatDateTime(item.confirmed_at);

        if (!submittedAt && !confirmedAt) return null;

        return (
            <div className={`indicator-filled-card__timeline${compact ? ' indicator-filled-card__timeline--compact' : ''}`}>
                {submittedAt && (
                    <div className="indicator-filled-card__date-row">
                        <ClockCircleOutlined className="indicator-filled-card__date-icon indicator-filled-card__date-icon--submitted" />
                        <div>
                            <Text type="secondary" className="indicator-filled-card__date-label">Pateikta</Text>
                            <Text>{submittedAt}</Text>
                        </div>
                    </div>
                )}
                {confirmedAt && (
                    <div className="indicator-filled-card__date-row">
                        <CheckCircleOutlined className="indicator-filled-card__date-icon indicator-filled-card__date-icon--confirmed" />
                        <div>
                            <Text type="secondary" className="indicator-filled-card__date-label">Patvirtinta</Text>
                            <Text>{confirmedAt}</Text>
                            {item.confirmed_by_user_name && (
                                <div className="indicator-filled-card__confirmed-by">
                                    <UserOutlined />
                                    <Text type="secondary">{item.confirmed_by_user_name}</Text>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }, [formatDateTime]);

    const formatCollectionDate = useCallback((item: SchoolCalendarIndicator, field: 'collection_start' | 'collection_end') => {
        const value = field === 'collection_start' ? item.collection_start : item.collection_end;
        const date = toCollectionDate(item, value);
        return date ? date.format('YYYY-MM-DD') : (value || '-');
    }, [toCollectionDate]);

    const renderIndicatorRowInfo = useCallback((item: SchoolCalendarIndicator) => (
        <div className="indicator-calendar-row__info">
            <div className="indicator-calendar-row__title-row">
                <Tag className="indicator-calendar-row__id">ID {item.id}</Tag>
                <Text strong className="indicator-calendar-row__title">{item.title}</Text>
            </div>
            <div className="indicator-calendar-row__categories">
                <Text type="secondary" className="indicator-calendar-row__categories-label">Kategorijos:</Text>
                {renderCategoryTags(item)}
            </div>
            <Text type="secondary" className="indicator-calendar-row__period">
                {item.period_description || item.period || '-'}
            </Text>
        </div>
    ), [renderCategoryTags]);

    const renderFilledIndicatorRow = useCallback((item: SchoolCalendarIndicator) => {
        const submittedAt = formatDateTime(item.submitted_at);
        const confirmedAt = formatDateTime(item.confirmed_at);

        return (
            <div className="indicator-calendar-row indicator-calendar-row--filled">
                {renderIndicatorRowInfo(item)}

                <div className="indicator-calendar-row__value">
                    <Text className="indicator-calendar-row__value-number">{item.value ?? '-'}</Text>
                    {(item.unit_description || item.unit) && (
                        <Text type="secondary" className="indicator-calendar-row__value-unit">
                            {item.unit_description || item.unit}
                        </Text>
                    )}
                </div>

                <div className="indicator-calendar-row__dates">
                    <div className="indicator-calendar-row__date">
                        <CalendarOutlined />
                        <Text type="secondary">
                            Rinkimas: {formatCollectionDate(item, 'collection_start')} – {formatCollectionDate(item, 'collection_end')}
                        </Text>
                    </div>
                    {submittedAt && (
                        <div className="indicator-calendar-row__date">
                            <ClockCircleOutlined />
                            <Text type="secondary">Pateikta: {submittedAt}</Text>
                        </div>
                    )}
                    {confirmedAt && (
                        <div className="indicator-calendar-row__date">
                            <CheckCircleOutlined />
                            <Text type="secondary">
                                Patvirtinta: {confirmedAt}
                                {item.confirmed_by_user_name ? ` · ${item.confirmed_by_user_name}` : ''}
                            </Text>
                        </div>
                    )}
                </div>

                <div className="indicator-calendar-row__status">
                    {renderStatusTag(item)}
                </div>
            </div>
        );
    }, [formatCollectionDate, formatDateTime, renderIndicatorRowInfo, renderStatusTag]);

    const renderOverdueIndicatorRow = useCallback((item: SchoolCalendarIndicator) => (
        <div className="indicator-calendar-row indicator-calendar-row--overdue">
            {renderIndicatorRowInfo(item)}

            <div className="indicator-calendar-row__highlight indicator-calendar-row__highlight--danger">
                <Text type="secondary" className="indicator-calendar-row__highlight-label">Terminas</Text>
                <Text strong className="indicator-calendar-row__highlight-value">
                    {formatCollectionDate(item, 'collection_end')}
                </Text>
            </div>

            <div className="indicator-calendar-row__dates">
                <div className="indicator-calendar-row__date">
                    <CalendarOutlined />
                    <Text type="secondary">
                        Rinkimas: {formatCollectionDate(item, 'collection_start')} – {formatCollectionDate(item, 'collection_end')}
                    </Text>
                </div>
            </div>

            <div className="indicator-calendar-row__status">
                <Tag color="red">Praleista</Tag>
            </div>
        </div>
    ), [formatCollectionDate, renderIndicatorRowInfo]);

    const renderActiveIndicatorRow = useCallback((item: SchoolCalendarIndicator) => (
        <div className="indicator-calendar-row indicator-calendar-row--active">
            {renderIndicatorRowInfo(item)}

            <div className="indicator-calendar-row__highlight indicator-calendar-row__highlight--warning">
                <Text type="secondary" className="indicator-calendar-row__highlight-label">Terminas iki</Text>
                <Text strong className="indicator-calendar-row__highlight-value">
                    {formatCollectionDate(item, 'collection_end')}
                </Text>
            </div>

            <div className="indicator-calendar-row__dates">
                <div className="indicator-calendar-row__date">
                    <CalendarOutlined />
                    <Text type="secondary">
                        Rinkimas: {formatCollectionDate(item, 'collection_start')} – {formatCollectionDate(item, 'collection_end')}
                    </Text>
                </div>
            </div>

            <div className="indicator-calendar-row__status">
                <Tag color="orange">Reikia vesti</Tag>
            </div>
        </div>
    ), [formatCollectionDate, renderIndicatorRowInfo]);

    const renderUpcomingIndicatorRow = useCallback((item: SchoolCalendarIndicator) => (
        <div className="indicator-calendar-row indicator-calendar-row--upcoming">
            {renderIndicatorRowInfo(item)}

            <div className="indicator-calendar-row__highlight indicator-calendar-row__highlight--info">
                <Text type="secondary" className="indicator-calendar-row__highlight-label">Pradžia</Text>
                <Text strong className="indicator-calendar-row__highlight-value">
                    {formatCollectionDate(item, 'collection_start')}
                </Text>
            </div>

            <div className="indicator-calendar-row__dates">
                <div className="indicator-calendar-row__date">
                    <CalendarOutlined />
                    <Text type="secondary">
                        Pabaiga: {formatCollectionDate(item, 'collection_end')}
                    </Text>
                </div>
            </div>

            <div className="indicator-calendar-row__status">
                <Tag color="blue">Artėja</Tag>
            </div>
        </div>
    ), [formatCollectionDate, renderIndicatorRowInfo]);

    const renderFilledIndicatorCard = useCallback((item: SchoolCalendarIndicator) => (
        <div className="indicator-filled-card indicator-filled-card--compact">
            <div className="indicator-filled-card__header">
                <div className="indicator-filled-card__title-row">
                    <Tag className="indicator-filled-card__id">ID {item.id}</Tag>
                    <Text strong className="indicator-filled-card__title">{item.title}</Text>
                </div>
                {renderStatusTag(item)}
            </div>

            <div className="indicator-filled-card__meta">
                <div className="indicator-filled-card__categories">
                    <Text type="secondary" className="indicator-filled-card__categories-label">Kategorijos:</Text>
                    {renderCategoryTags(item)}
                </div>
                <Text type="secondary" className="indicator-filled-card__period">
                    {item.period_description || item.period || '-'}
                </Text>
            </div>

            <div className="indicator-filled-card__value-block">
                <Text className="indicator-filled-card__value">{item.value ?? '-'}</Text>
                {(item.unit_description || item.unit) && (
                    <Text type="secondary" className="indicator-filled-card__unit">
                        {item.unit_description || item.unit}
                    </Text>
                )}
            </div>

            <Text type="secondary" className="indicator-filled-card__collection">
                Rinkimas: {formatCollectionDate(item, 'collection_start')} – {formatCollectionDate(item, 'collection_end')}
            </Text>

            {renderSubmissionTimeline(item, true)}
        </div>
    ), [formatCollectionDate, renderCategoryTags, renderStatusTag, renderSubmissionTimeline]);

    const renderModalIndicatorItem = useCallback((item: SchoolCalendarIndicator) => {
        if (hasValue(item)) {
            return renderFilledIndicatorCard(item);
        }

        return (
            <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <Space wrap size={[4, 4]}>
                        <Tag>ID {item.id}</Tag>
                        <div style={{ fontWeight: 600 }}>{item.title}</div>
                    </Space>
                    {renderStatusTag(item)}
                </div>
                <div style={{ color: '#8c8c8c', marginTop: 4 }}>
                    Kategorijos: {renderCategories(item)}
                </div>
                <div style={{ color: '#8c8c8c' }}>
                    Rinkimas: {formatCollectionDate(item, 'collection_start')} – {formatCollectionDate(item, 'collection_end')} | Periodas: {item.period_description || item.period || '-'}
                </div>
            </div>
        );
    }, [formatCollectionDate, hasValue, renderCategories, renderFilledIndicatorCard, renderStatusTag]);

    const selectedMonthLabel = useMemo(() => {
        const raw = panelDate.format('YYYY MMMM');
        const [year, ...monthParts] = raw.split(' ');
        const month = monthParts.join(' ');
        const monthCap = month ? `${month.charAt(0).toUpperCase()}${month.slice(1)}` : '';
        return [year, monthCap].filter(Boolean).join(' ');
    }, [panelDate]);

    return (
        <Card>
            <Title level={4} style={{ marginTop: 0 }}>
                Rodiklių kalendorius
            </Title>

            <Spin spinning={isFetching}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Card
                            className="school-indicators-calendar"
                            bodyStyle={{ padding: 12 }}
                            style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                                {canFilterCritical && !department && (
                                    <Space>
                                        <Text>Tik kritiniai rodikliai</Text>
                                        <Switch
                                            checked={onlyCritical}
                                            onChange={setOnlyCritical}
                                            disabled={!basePayload} />
                                    </Space>
                                )}
                                <Button
                                    type="primary"
                                    icon={<ReloadOutlined />}
                                    onClick={fetchCalendar}
                                    loading={isFetching}
                                    disabled={!basePayload}>
                                    Atnaujinti
                                </Button>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 16,
                                    marginBottom: 8,
                                }}>
                                <Button
                                    type="text"
                                    icon={<LeftOutlined />}
                                    aria-label="Ankstesnis mėnuo"
                                    onClick={goToPreviousMonth} />
                                <Title level={5} style={{ margin: 0 }}>
                                    {selectedMonthLabel}
                                </Title>
                                <Button
                                    type="text"
                                    icon={<RightOutlined />}
                                    aria-label="Kitas mėnuo"
                                    onClick={goToNextMonth} />
                            </div>
                            <Calendar
                                fullscreen
                                value={panelDate}
                                onPanelChange={handlePanelChange}
                                fullCellRender={fullCellRender} />
                        </Card>
                    </Col>

                    {schoolCalendarIndicators?.length ? (
                        <Col span={24}>
                            <Card className="indicator-calendar-search" bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={10}>
                                        <Input
                                            allowClear
                                            prefix={<SearchOutlined />}
                                            placeholder="Ieškoti pagal ID ar pavadinimą"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)} />
                                    </Col>
                                    <Col xs={24} md={10}>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Filtruoti pagal kategoriją"
                                            value={selectedCategoryId}
                                            onChange={(value) => setSelectedCategoryId(value)}
                                            options={categoryOptions}
                                            style={{ width: '100%' }} />
                                    </Col>
                                    <Col xs={24} md={4}>
                                        <Button
                                            block
                                            onClick={handleClearSearch}
                                            disabled={!hasActiveSearch}>
                                            Išvalyti
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    ) : null}

                    <Col span={24}>
                        {!schoolCalendarIndicators?.length ? (
                            <>
                                <Paragraph style={{ marginBottom: 12 }}>
                                    Kol kas nėra rodiklių šiame kalendoriuje (arba dar neužkrauta).
                                </Paragraph>
                                <Empty description="Nėra duomenų" />
                            </>
                        ) : (
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Card
                                        title="Praleisti suvedimai (nedelsiant suvesti)"
                                        style={{ borderColor: '#ffccc7' }}
                                        headStyle={{ background: '#fff1f0' }}>
                                        {filteredOverdueIndicators.length ? (
                                            <List
                                                className="indicator-calendar-list"
                                                dataSource={filteredOverdueIndicators}
                                                renderItem={(item: SchoolCalendarIndicator) => (
                                                    <List.Item className="indicator-calendar-list__item">
                                                        {renderOverdueIndicatorRow(item)}
                                                    </List.Item>
                                                )} />
                                        ) : (
                                            <Empty description={getFilteredEmptyDescription('Nėra praleistų terminų')} />
                                        )}
                                    </Card>
                                </Col>

                                <Col span={24}>
                                    <Card
                                        title="Vedami rodikliai (reikia vesti dabar)"
                                        style={{ borderColor: '#ffe58f' }}
                                        headStyle={{ background: '#fffbe6' }}>
                                        {filteredActiveIndicators.length ? (
                                            <List
                                                className="indicator-calendar-list"
                                                dataSource={filteredActiveIndicators}
                                                renderItem={(item: SchoolCalendarIndicator) => (
                                                    <List.Item className="indicator-calendar-list__item">
                                                        {renderActiveIndicatorRow(item)}
                                                    </List.Item>
                                                )} />
                                        ) : (
                                            <Empty description={getFilteredEmptyDescription('Nėra šiuo metu vedamų rodiklių')} />
                                        )}
                                    </Card>
                                </Col>

                                <Col span={24}>
                                    <Card
                                        title="Artėjantys suvedimai (terminas dar nepriėjo)"
                                        style={{ borderColor: '#91caff' }}
                                        headStyle={{ background: '#e6f4ff' }}>
                                        {filteredUpcomingIndicators.length ? (
                                            <List
                                                className="indicator-calendar-list"
                                                dataSource={filteredUpcomingIndicators}
                                                renderItem={(item: SchoolCalendarIndicator) => (
                                                    <List.Item className="indicator-calendar-list__item">
                                                        {renderUpcomingIndicatorRow(item)}
                                                    </List.Item>
                                                )} />
                                        ) : (
                                            <Empty description={getFilteredEmptyDescription('Nėra artėjančių suvedimų')} />
                                        )}
                                    </Card>
                                </Col>

                                <Col span={24}>
                                    <Card
                                        title="Suvesti rodikliai"
                                        style={{ borderColor: '#b7eb8f' }}
                                        headStyle={{ background: '#f6ffed' }}>
                                        {filteredCompletedIndicators.length ? (
                                            <List
                                                className="indicator-calendar-list"
                                                dataSource={filteredCompletedIndicators}
                                                renderItem={(item: SchoolCalendarIndicator) => (
                                                    <List.Item className="indicator-calendar-list__item">
                                                        {renderFilledIndicatorRow(item)}
                                                    </List.Item>
                                                )} />
                                        ) : (
                                            <Empty description={getFilteredEmptyDescription('Nėra suvestų rodiklių')} />
                                        )}
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
            </Spin>

            <Modal
                title={`Rodikliai dienai: ${selectedDate.format('YYYY-MM-DD')}`}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={820}>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Card
                            size="small"
                            title={`Rodikliai, kurių rinkimas vyksta šią dieną (${selectedDayActiveIndicators.length})`}
                            style={{ borderColor: '#91d5ff' }}
                            headStyle={{ background: '#e6f7ff' }}>
                            {selectedDayActiveIndicators.length ? (
                                <List
                                    size="small"
                                    dataSource={selectedDayActiveIndicators}
                                    renderItem={(item: SchoolCalendarIndicator) => (
                                        <List.Item>
                                            {renderModalIndicatorItem(item)}
                                        </List.Item>
                                    )} />
                            ) : (
                                <Empty description="Šią dieną nevyksta jokio rodiklio rinkimas" />
                            )}
                        </Card>
                    </Col>

                    <Col span={24} md={12}>
                        <Card size="small" title={`Rodikliai, kurių rinkimas prasideda (${selectedBucket.start.length})`}>
                            {selectedBucket.start.length ? (
                                <List
                                    size="small"
                                    dataSource={selectedBucket.start}
                                    renderItem={(item: SchoolCalendarIndicator) => (
                                        <List.Item>
                                            {renderModalIndicatorItem(item)}
                                        </List.Item>
                                    )} />
                            ) : (
                                <Empty description="Šią dieną neprasideda jokio rodiklio rinkimas" />
                            )}
                        </Card>
                    </Col>

                    <Col span={24} md={12}>
                        <Card size="small" title={`Rodikliai, kurių rinkimo terminas baigiasi (${selectedBucket.end.length})`}>
                            {selectedBucket.end.length ? (
                                <List
                                    size="small"
                                    dataSource={selectedBucket.end}
                                    renderItem={(item: SchoolCalendarIndicator) => (
                                        <List.Item>
                                            {renderModalIndicatorItem(item)}
                                        </List.Item>
                                    )} />
                            ) : (
                                <Empty description="Šią dieną nesibaigia jokio rodiklio rinkimo terminas" />
                            )}
                        </Card>
                    </Col>
                </Row>
            </Modal>
        </Card>
    );
};

SchoolIndicatorsCalendar.defaultProps = {
    department: false,
};

export default SchoolIndicatorsCalendar;
